import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import type { Check } from '../types'
import type { CheckPayload } from '@app/shared/types/sf-data'
import { isResponseRetry } from '@app/utils/predicates'

import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'
import type { ProgressStatus } from '@app/application/sf-assistant-checks/constants'
import { progressStatus } from '@app/application/sf-assistant-checks/constants'

import { useSfAssistantChecksReadStatus } from '@app/application/sf-assistant-checks/stores/assistant-checks-read-status'
import { useSfAssistantChecksCommentIds } from '@app/application/sf-assistant-checks/stores/assistant-checks-comment-ids'

export const useChecksStore = defineStore('checksStore', () => {
  const accountId = ref<number | undefined>()
  const reconciliationId = ref<number | undefined>()
  const showGeneral = ref<boolean>(false)
  const checks = ref<Check[]>([])
  const timestamp = ref<Date | undefined>()
  const loadingStatus = ref<ProgressStatus>(progressStatus.NONE)
  const setupStatus = ref<ProgressStatus>(progressStatus.NONE)
  const error = ref<boolean>(false)
  const assistantChecksReadStatusStore = useSfAssistantChecksReadStatus()
  const { setReadStatusChecks, setReadByUsersChecks } =
    assistantChecksReadStatusStore

  const assistantChecksCommentIdsStore = useSfAssistantChecksCommentIds()
  const { setCommentIds } = assistantChecksCommentIdsStore

  function startLoading(): void {
    loadingStatus.value = progressStatus.STARTED
  }

  function stopLoading(): void {
    loadingStatus.value = progressStatus.DONE
  }

  function refreshChecks(): boolean {
    return true
  }

  async function receiveChecks(): Promise<void> {
    if (loadingStatus.value === progressStatus.STARTED) {
      return
    }

    if (!accountId.value && !reconciliationId.value && !showGeneral.value) {
      return
    }

    startLoading()

    const route = 'checks_ledger_assistant_checks_v2_index_path'

    let options: Record<string, boolean | number>
    if (accountId.value) {
      options = { account_id: accountId.value }
    } else if (reconciliationId.value) {
      options = { reconciliation_id: reconciliationId.value }
    } else {
      options = { general: true }
    }

    const path = window.Silverfin.Routes[route](
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      window.Silverfin.currentLedgerId(),
      options
    )

    try {
      const checksPayload = await SFetch.json<CheckPayload>(path, {
        method: 'GET',
      })

      if (checksPayload) {
        if (accountId.value) {
          checks.value = checksPayload['accounts'][accountId.value] || []
        }
        if (reconciliationId.value) {
          checks.value =
            checksPayload['reconciliations'][reconciliationId.value] || []
        }
        if (showGeneral.value) {
          checks.value = checksPayload['general'] || []
        }

        setReadStatusChecks(checksPayload.readStatus)
        setReadByUsersChecks(checksPayload.readByUsers)
        setCommentIds(checks.value)

        timestamp.value = checksPayload['timestamp']
        stopLoading()
      } else {
        checks.value = []
        timestamp.value = undefined
        stopLoading()
      }
    } catch (err) {
      SfLog.warn('Error fetching checks', {
        extra: {
          error: err,
        },
      })

      checks.value = []
      timestamp.value = undefined
      stopLoading()
    }
  }

  async function triggerCalculation(): Promise<void> {
    const route = 'trigger_ledger_assistant_checks_v2_index_path'

    if (loadingStatus.value !== progressStatus.STARTED) {
      const url = window.Silverfin.Routes[route](
        window.Silverfin.currentFirmId,
        window.Silverfin.currentCompanyId,
        window.Silverfin.currentLedgerId()
      )

      try {
        await SFetch.withRetry(SFetch.json, {
          maxRetries: 5,
          shouldRetry: isResponseRetry,
        })(url, { method: 'POST', body: {} })
      } catch (error_) {
        if (error_ === 'SFetch::WithRetry::Max retries exceeded') {
          SfLog.info(
            'Max retries exceeded for Silverfin Assistant checks calculation',
            {
              extra: {
                companyId: window.Silverfin.currentCompanyId,
                firmId: window.Silverfin.currentFirmId,
              },
            }
          )
        } else {
          SfLog.warn('Error triggering calculation', {
            extra: {
              error: error_,
            },
          })
        }
      }
    }
  }

  async function resetChecks(
    contextType: string,
    contextId: number
  ): Promise<void> {
    accountId.value = undefined
    reconciliationId.value = undefined
    checks.value = []

    if (contextType === 'account') {
      accountId.value = contextId
    } else {
      reconciliationId.value = contextId
    }

    stopLoading()
    await receiveChecks()
  }

  const hasChecks = computed<boolean>(() => checks.value.length > 0)

  const unreadChecks = computed<Check[]>(() => {
    const { checkReadStatus } = assistantChecksReadStatusStore
    return checks.value.filter((check) => !checkReadStatus[check.id])
  })

  const totalChecks = computed<number>(() => checks.value.length)

  return {
    // state
    accountId,
    reconciliationId,
    showGeneral,
    checks,
    timestamp,
    loadingStatus,
    setupStatus,
    error,

    // actions
    startLoading,
    stopLoading,
    refreshChecks,
    receiveChecks,
    triggerCalculation,
    resetChecks,

    // getters
    hasChecks,
    totalChecks,
    unreadChecks,
  }
})
